"use client";

import React, { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Search,
  Globe,
  Server,
  Layers,
  Hammer,
  Box,
  Database,
  Terminal,
  ShieldCheck,
  Settings,
  X,
  ArrowRight,
  Mail,
  Network,
  KeyRound,
  FolderOpen,
  PlusCircle,
  RotateCw,
  Users,
  HardDrive,
  Cloud,
  Palette,
} from "lucide-react";
import { CustomerCommandPalette } from "./CustomerCommandPalette";
import { useAuth } from "@/lib/auth/context";
import { isPlatformAdminRole } from "@/lib/auth/roles";
import { useTheme } from "@/lib/theme/context";
import { THEMES, ThemeId } from "@/lib/theme/themes";

interface CommandItem {
  id: string;
  title: string;
  subtitle: string;
  category: "Themes" | "Websites" | "Accounts" | "Domains" | "Clusters" | "Databases" | "Navigation" | "Actions";
  href?: string;
  action?: () => void;
  icon: React.ElementType;
}

export function CommandPalette(props: { isOpen: boolean; onClose: () => void; onOpen: () => void }) {
  const { user } = useAuth();
  return isPlatformAdminRole(user?.role) ? <AdminCommandPalette {...props} /> : <CustomerCommandPalette {...props} />;
}

function AdminCommandPalette({
  isOpen,
  onClose,
  onOpen,
}: {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dynamicItems, setDynamicItems] = useState<CommandItem[]>([]);
  const router = useRouter();
  const { user: authUser } = useAuth();
  const { setTheme } = useTheme();
  const isSuperAdmin = isPlatformAdminRole(authUser?.role);
  const listRef = useRef<HTMLDivElement>(null);

  // Fetch dynamic entities (sites, accounts, clusters, databases, domains) when palette opens
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSelectedIndex(0);
      return;
    }

    const fetchSearchEntities = async () => {
      try {
        const fetchedItems: CommandItem[] = [];

        if (isSuperAdmin) {
          const [sitesRes, accountsRes, clustersRes, dbRes, domainsRes] = await Promise.allSettled([
            fetch("/api/sites"),
            fetch("/api/accounts"),
            fetch("/api/clusters"),
            fetch("/api/infrastructure/databases"),
            fetch("/api/domains"),
          ]);

          if (sitesRes.status === "fulfilled" && sitesRes.value.ok) {
            const data = await sitesRes.value.json();
            const siteList = Array.isArray(data?.sites) ? data.sites : [];
            siteList.slice(0, 25).forEach((s: any) => {
              fetchedItems.push({
                id: `site-${s.id}`,
                title: s.domain,
                subtitle: `${s.name} · ${s.status} · ${s.cluster?.name || "Cluster"}`,
                category: "Websites",
                href: `/sites/${s.id}`,
                icon: Globe,
              });
            });
          }

          if (accountsRes.status === "fulfilled" && accountsRes.value.ok) {
            const data = await accountsRes.value.json();
            const accList = Array.isArray(data) ? data : Array.isArray(data?.accounts) ? data.accounts : [];
            accList.slice(0, 15).forEach((a: any) => {
              fetchedItems.push({
                id: `acc-${a.id}`,
                title: a.name || a.slug,
                subtitle: `Plan: ${a.package?.name || "Default"} · ${a.slug}`,
                category: "Accounts",
                href: `/accounts/${a.id}`,
                icon: Users,
              });
            });
          }

          if (clustersRes.status === "fulfilled" && clustersRes.value.ok) {
            const data = await clustersRes.value.json();
            const clList = Array.isArray(data?.clusters) ? data.clusters : [];
            clList.forEach((c: any) => {
              fetchedItems.push({
                id: `cluster-${c.id}`,
                title: c.name,
                subtitle: `Status: ${c.status} · ${c.nodeCount || 0} nodes · ${c.isK3s ? "K3s" : "K8s"}`,
                category: "Clusters",
                href: `/clusters`,
                icon: Server,
              });
            });
          }

          if (dbRes.status === "fulfilled" && dbRes.value.ok) {
            const data = await dbRes.value.json();
            const dbList = Array.isArray(data?.servers) ? data.servers : [];
            dbList.forEach((d: any) => {
              fetchedItems.push({
                id: `db-${d.id}`,
                title: d.name,
                subtitle: `${d.engine || "MySQL"} · ${d.host}:${d.port} · ${d.status}`,
                category: "Databases",
                href: `/infrastructure/databases`,
                icon: Database,
              });
            });
          }

          if (domainsRes.status === "fulfilled" && domainsRes.value.ok) {
            const data = await domainsRes.value.json();
            const domList = Array.isArray(data) ? data : Array.isArray(data?.domains) ? data.domains : [];
            domList.slice(0, 15).forEach((d: any) => {
              fetchedItems.push({
                id: `dom-${d.id}`,
                title: d.domain,
                subtitle: `Target: ${d.site?.domain || "Apex"} · Status: ${d.status || "Active"}`,
                category: "Domains",
                href: `/domains?domainId=${d.id}`,
                icon: Globe,
              });
            });
          }
        } else {
          // Customer search (strictly tenant-scoped)
          const [sitesRes, domainsRes] = await Promise.allSettled([
            fetch("/api/portal/sites"),
            fetch("/api/domains"),
          ]);

          if (sitesRes.status === "fulfilled" && sitesRes.value.ok) {
            const data = await sitesRes.value.json();
            const siteList = Array.isArray(data?.sites) ? data.sites : [];
            siteList.forEach((s: any) => {
              fetchedItems.push({
                id: `site-${s.id}`,
                title: s.domain,
                subtitle: `${s.name} · ${s.status}`,
                category: "Websites",
                href: `/portal/sites/${s.id}`,
                icon: Globe,
              });
            });
          }

          if (domainsRes.status === "fulfilled" && domainsRes.value.ok) {
            const data = await domainsRes.value.json();
            const domList = Array.isArray(data) ? data : Array.isArray(data?.domains) ? data.domains : [];
            domList.forEach((d: any) => {
              fetchedItems.push({
                id: `dom-${d.id}`,
                title: d.domain,
                subtitle: `Status: ${d.status || "Active"}`,
                category: "Domains",
                href: `/portal/dns`,
                icon: Globe,
              });
            });
          }
        }

        setDynamicItems(fetchedItems);
      } catch {
        // Non-fatal fallback
      }
    };

    fetchSearchEntities();
  }, [isOpen, isSuperAdmin]);

  // Theme Commands
  const themeCommands: CommandItem[] = THEMES.map((t) => ({
    id: `theme-${t.id}`,
    title: `Switch Theme: ${t.name}`,
    subtitle: `${t.category} Mode · ${t.description}`,
    category: "Themes",
    action: () => setTheme(t.id as ThemeId),
    icon: Palette,
  }));

  const adminStaticCommands: CommandItem[] = [
    { id: "new-site", title: "Deploy New Website", subtitle: "Launch the streamlined site creation wizard", category: "Actions", href: "/sites/new", icon: PlusCircle },
    { id: "sites", title: "Managed Websites", subtitle: "Inspect fleet workloads and live statuses", category: "Navigation", href: "/sites", icon: Globe },
    { id: "accounts", title: "Customer Accounts", subtitle: "Manage tenant organizations and quotas", category: "Navigation", href: "/accounts", icon: Users },
    { id: "members", title: "Team Members & Security", subtitle: "Passkeys, 2FA, and user accounts", category: "Navigation", href: "/system/members", icon: Users },
    { id: "domains", title: "Domain Portfolio", subtitle: "Inspect apex domains and DNS routing", category: "Navigation", href: "/domains", icon: Globe },
    { id: "customer-dns", title: "Customer DNS Zones", subtitle: "Customer authoritative DNS zones and records", category: "Navigation", href: "/dns", icon: Network },
    { id: "clusters", title: "Clusters & Infrastructure", subtitle: "Manage Kubernetes clusters and nodes", category: "Navigation", href: "/clusters", icon: Server },
    { id: "templates", title: "Application Blueprints", subtitle: "WordPress, PHP, and Next.js templates", category: "Navigation", href: "/templates", icon: Layers },
    { id: "databases", title: "Databases & Servers", subtitle: "MySQL and PostgreSQL instances", category: "Navigation", href: "/infrastructure/databases", icon: Database },
    { id: "mail", title: "Email Infrastructure", subtitle: "Stalwart mail accounts and JMAP sync", category: "Navigation", href: "/infrastructure/mail", icon: Mail },
    { id: "dns", title: "DNS (PowerDNS)", subtitle: "Authoritative zones and DNSSEC", category: "Navigation", href: "/infrastructure/dns", icon: Network },
    { id: "cloudflare", title: "Cloudflare Edge", subtitle: "Edge caching, DNS, and SSL", category: "Navigation", href: "/infrastructure/cloudflare", icon: Cloud },
    { id: "backups", title: "Backup & Restore", subtitle: "S3 and Google Drive backup archives", category: "Navigation", href: "/infrastructure/backup", icon: HardDrive },
    { id: "backup-jobs", title: "Backup Jobs", subtitle: "All backup, restore, and schedule job status", category: "Navigation", href: "/infrastructure/backup/jobs", icon: HardDrive },
    { id: "builds", title: "Image Build Center", subtitle: "Harbor OCI builds and runners", category: "Navigation", href: "/builds", icon: Hammer },
    { id: "harbor", title: "Harbor Registry", subtitle: "Browse image repositories and digests", category: "Navigation", href: "/harbor", icon: Box },
    { id: "reconciliation", title: "Cluster Reconciliation", subtitle: "Scan drift and reconcile cluster state", category: "Actions", href: "/reconciliation", icon: RotateCw },
    { id: "logs", title: "Live Pod Terminal Logs", subtitle: "Real-time container stdout logs", category: "Navigation", href: "/logs", icon: Terminal },
    { id: "debug", title: "Debug Console", subtitle: "In-app diagnostic log streaming", category: "Navigation", href: "/infrastructure/debug", icon: Terminal },
    { id: "audit", title: "Audit Trail", subtitle: "Immutable log of infrastructure operations", category: "Navigation", href: "/audit", icon: ShieldCheck },
    { id: "settings", title: "Settings & Credentials Vault", subtitle: "Configure secrets and system paths", category: "Navigation", href: "/settings", icon: Settings },
  ];

  const portalStaticCommands: CommandItem[] = [
    { id: "portal-dashboard", title: "Hosting Dashboard", subtitle: "Account overview & resource summary", category: "Navigation", href: "/portal", icon: Globe },
    { id: "portal-sites", title: "My Websites", subtitle: "Manage active websites, SSL, and staging", category: "Navigation", href: "/portal/sites", icon: Globe },
    { id: "portal-files", title: "File Manager", subtitle: "Browse, upload, and edit website files", category: "Navigation", href: "/portal/files", icon: FolderOpen },
    { id: "portal-mail", title: "Email Accounts", subtitle: "Manage mailboxes, aliases, and forwarders", category: "Navigation", href: "/portal/mail", icon: Mail },
    { id: "portal-dns", title: "Domains & DNS", subtitle: "Manage DNS records for your domains", category: "Navigation", href: "/portal/dns", icon: Network },
    { id: "portal-databases", title: "Databases", subtitle: "View database credentials and connection info", category: "Navigation", href: "/portal/databases", icon: Database },
    { id: "portal-security", title: "Security & Passkeys", subtitle: "Passkeys, TOTP 2FA, and sessions", category: "Navigation", href: "/security", icon: KeyRound },
  ];

  const staticCommands = isSuperAdmin ? adminStaticCommands : portalStaticCommands;
  const allCommands = [...dynamicItems, ...staticCommands, ...themeCommands];

  const filtered = allCommands.filter(
    (c) =>
      c.title.toLowerCase().includes(query.toLowerCase()) ||
      c.subtitle.toLowerCase().includes(query.toLowerCase()) ||
      c.category.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (item: CommandItem) => {
    if (item.action) {
      item.action();
    } else if (item.href) {
      router.push(item.href);
    }
    onClose();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (isOpen) onClose();
        else onOpen();
      } else if (isOpen) {
        if (e.key === "Escape") {
          onClose();
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (filtered[selectedIndex]) {
            handleSelect(filtered[selectedIndex]);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onOpen, filtered, selectedIndex]);

  // Ensure selected item is scrolled into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-surface-overlay backdrop-blur-sm flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-surface-elevated border border-themed-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 text-themed-textPrimary"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Header */}
        <div className="flex items-center px-4 py-3.5 border-b border-themed-borderSubtle gap-3 bg-surface-muted/40">
          <Search className="w-5 h-5 text-[var(--accent-primary)] shrink-0" />
          <input
            type="text"
            placeholder={
              isSuperAdmin
                ? "Search websites, themes, accounts, clusters, databases, actions..."
                : "Search your websites, themes, files, mailboxes, databases..."
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            autoFocus
            className="w-full bg-transparent text-themed-textPrimary placeholder:text-themed-textMuted text-xs focus:outline-none font-medium"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="p-1 rounded text-themed-textMuted hover:text-themed-textPrimary"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-surface-muted border border-themed-border rounded-md text-themed-textMuted font-semibold shadow-sm">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div ref={listRef} className="max-h-96 overflow-y-auto p-2 space-y-1">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-xs text-themed-textMuted">
              No matching resources, themes, or actions found for &ldquo;{query}&rdquo;.
            </div>
          ) : (
            filtered.map((item, idx) => {
              const Icon = item.icon;
              const isSelected = idx === selectedIndex;
              return (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full flex items-center justify-between p-2.5 rounded-xl transition-all text-left cursor-pointer ${
                    isSelected
                      ? "bg-[var(--accent-primary)]/10 text-themed-textPrimary border border-[var(--accent-primary)]/30 font-semibold"
                      : "text-themed-textSecondary hover:bg-surface-cardHover hover:text-themed-textPrimary border border-transparent"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`p-2 rounded-lg shrink-0 transition-colors ${
                        isSelected
                          ? "bg-[var(--accent-primary)]/20 text-[var(--accent-primary)]"
                          : "bg-surface-muted text-themed-textMuted"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold truncate text-themed-textPrimary">{item.title}</h4>
                      <p className="text-[11px] text-themed-textSecondary truncate">{item.subtitle}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className="text-[10px] px-2 py-0.5 rounded bg-surface-muted border border-themed-border text-themed-textMuted font-mono font-semibold">
                      {item.category}
                    </span>
                    <ArrowRight
                      className={`w-3.5 h-3.5 transition-colors ${
                        isSelected ? "text-[var(--accent-primary)] translate-x-0.5" : "text-themed-textMuted opacity-40"
                      }`}
                    />
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer with Keyboard Hints */}
        <div className="px-4 py-2.5 bg-surface-muted/30 border-t border-themed-borderSubtle flex items-center justify-between text-[11px] text-themed-textMuted font-medium">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-surface-muted border border-themed-border text-[10px] font-mono">↑</kbd>
              <kbd className="px-1 py-0.5 rounded bg-surface-muted border border-themed-border text-[10px] font-mono">↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 rounded bg-surface-muted border border-themed-border text-[10px] font-mono">↵</kbd>
              Select
            </span>
          </div>
          <span>{filtered.length} item{filtered.length === 1 ? "" : "s"} found</span>
        </div>
      </div>
    </div>
  );
}
